'use client';

import { useState, useCallback } from 'react';

import { useSubmitDataIssueMutation as useSubmitDataIssue } from 'src/api/redux';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { Typography, PulseButton, Dialog } from 'src/components/PulseUI';

import { DataIssueTypeSelector } from './DataIssueTypeSelector';
import { DataIssueAddressSuggestions } from './DataIssueAddressSuggestions';
import { DataIssueEmailSuggestions } from './DataIssueEmailSuggestions';
import { DataIssueComments } from './DataIssueComments';
import { DataIssuePhone } from './DataIssuePhone';
import { AddressType, DataIssueTypes } from './types';

interface FormState {
  issueType: DataIssueTypes | null;
  addressData: AddressType | null;
  email: string | null;
  emailId: string | null;
  phone: string | null;
  comments: string;
}

const initialFormState: FormState = {
  issueType: null,
  addressData: null,
  email: null,
  emailId: null,
  phone: null,
  comments: '',
};

interface ReportDataIssueDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerType: string;
}

const ReportDataIssueDialog = ({
  open,
  onClose,
  providerId,
  providerType,
}: ReportDataIssueDialogProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const [submitDataIssue, { isLoading: isSubmitLoading }] =
    useSubmitDataIssue();
  const [form, setForm] = useState<FormState>(initialFormState);
  const isHcp = providerType === 'hcp';

  const updateForm = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const resetForm = useCallback(() => setForm(initialFormState), []);

  const handleIssueTypeSelection = (newValue: string) => {
    setForm({ ...initialFormState, issueType: newValue as DataIssueTypes });
  };

  const handleOnClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const { issueType, addressData, email, phone, comments } = form;
    if (!issueType) return;

    const dataToSubmit = {
      phone: phone ? { phone } : null,
      address: addressData,
      email: email ? { email } : null,
    };

    try {
      await submitDataIssue({
        issue_type: issueType.toUpperCase(),
        provider_id: providerId,
        new_data: dataToSubmit[issueType],
        comments,
      }).unwrap();

      handleOnClose();

      log.event('dataIssueReported', {
        provider_id: providerId,
        content_type: providerType,
        reason: issueType,
      });

      setNotification({
        title: 'Data Issue Submitted',
        message: 'Look out for an email from us soon!',
        type: 'success',
      });
    } catch (err) {
      setNotification({
        title: 'Error',
        message:
          err?.error_message ||
          'An error occurred while submitting data issue. Our team has been notified.',
        type: 'error',
      });
      log.exception(err);
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={handleOnClose}
      size="md"
      className="h-auto min-h-64 max-h-[80vh]"
      aria-labelledby="report-data-issue-dialog-title"
      data-testid="report-data-issue-dialog"
    >
      <Dialog.Header>
        <span id="report-data-issue-dialog-title">
          <Typography variant="h3">See something wrong?</Typography>
        </span>
      </Dialog.Header>
      <Dialog.Content
        className="p-4 space-y-4"
        data-testid="report-data-issue-content"
      >
        <div className="text-xs text-tertiary">
          Please select the type of data issue you have encountered, with any
          additional feedback or comments. We use this feedback to improve our
          profile enrichment process and will reach out to you when your issue
          has been resolved.
        </div>
        <DataIssueTypeSelector
          issueType={form.issueType}
          onChange={handleIssueTypeSelection}
        />
        {isHcp && form.issueType === 'address' && (
          <DataIssueAddressSuggestions
            providerId={providerId}
            setAddressData={(val) => updateForm('addressData', val)}
          />
        )}
        {isHcp && form.issueType === 'email' && (
          <DataIssueEmailSuggestions
            issueType={form.issueType}
            providerId={providerId}
            providerType={providerType}
            email={form.email ?? ''}
            setEmail={(val) => updateForm('email', val)}
            emailId={form.emailId ?? ''}
            setEmailId={(val) => updateForm('emailId', val)}
          />
        )}
        {form.issueType === 'phone' && (
          <DataIssuePhone
            phone={form.phone ?? ''}
            setPhone={(val) => updateForm('phone', val)}
          />
        )}
        {form.issueType && (
          <DataIssueComments
            comments={form.comments}
            setComments={(val) => updateForm('comments', val)}
          />
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          color="danger"
          variant="outline"
          onClick={handleOnClose}
          data-testid="report-data-issue-cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          disabled={!form.issueType}
          onClick={handleSubmit}
          loading={isSubmitLoading}
          data-testid="report-data-issue-submit-button"
        >
          Submit
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default ReportDataIssueDialog;
